import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { RateReview as ReviewIcon } from '@mui/icons-material';

function TassReviewProgress({ jobId, token, onComplete }) {
  const [status, setStatus] = useState('processing');
  const [progress, setProgress] = useState(0);
  const [clientCount, setClientCount] = useState(null);
  const intervalRef = useRef(null);

  const pollStatus = useCallback(async () => {
    if (!jobId || !token) return;
    try {
      // Inline fetch to avoid circular dependency — uses same base URL pattern as tassApi
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${API_BASE_URL}/tass/sessions/review-status/${jobId}`, {
        headers: { 'x-auth-token': token }
      });
      if (!response.ok) throw new Error('Failed to fetch review status');
      const data = await response.json();

      setStatus(data.status);
      setProgress(data.progress || 0);
      if (data.clientCount) setClientCount(data.clientCount);

      if (data.status === 'completed' || data.status === 'failed') {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (onComplete) onComplete(data);
      }
    } catch (err) {
      console.error('[TassReviewProgress] Poll error:', err);
    }
  }, [jobId, token, onComplete]);

  useEffect(() => {
    if (!jobId) return;
    pollStatus();
    intervalRef.current = setInterval(pollStatus, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [jobId, pollStatus]);

  if (status === 'completed') {
    return (
      <Box display="flex" alignItems="center" gap={1} sx={{ py: 0.5 }}>
        <ReviewIcon sx={{ fontSize: 16, color: 'success.main' }} />
        <Typography variant="caption" sx={{ color: 'success.main' }}>
          Review complete
        </Typography>
      </Box>
    );
  }

  if (status === 'failed') {
    return (
      <Box display="flex" alignItems="center" gap={1} sx={{ py: 0.5 }}>
        <ReviewIcon sx={{ fontSize: 16, color: 'error.main' }} />
        <Typography variant="caption" sx={{ color: 'error.main' }}>
          Review failed
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 0.5, width: '100%' }}>
      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
        <ReviewIcon sx={{ fontSize: 16, color: 'info.main' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Reviewing{clientCount ? ` ${clientCount} clients` : ''} — {Math.round(progress)}% complete
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ borderRadius: 1, height: 4 }}
      />
    </Box>
  );
}

export default TassReviewProgress;
